/* eslint-disable react/prop-types, react/jsx-handler-names */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from 'react-select';
import { Theme, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import NoSsr from '@material-ui/core/NoSsr';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import CancelIcon from '@material-ui/icons/Cancel';
import { emphasize } from '@material-ui/core/styles/colorManipulator';
import { SELECT_ALL } from '../Constants/constants';
import { QUESTION_CREATE_REPORTS } from '../../reports/container/ReportsServices';

const styles = (theme: any) => ({
  root: {
    flexGrow: 1,
    height: 10,
  },
  input: {
    display: 'flex',
    padding: 0,
    '& .css-1okebmr-indicatorSeparator':{
      background: 'transparent',
    }
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden !important',
    '&>div':{
      padding: 0,
      height: 30,
      lineHeight: '30px',
    },
    '&>div:last-of-type':{
      marginTop: '-8px',
    }
 
  },
  chip: {
    margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
    '& svg':{
      fill: '#666',
    }
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      0.08
    ),
  },
  noOptionsMessage: {
    padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    fontSize: 16,
  },
  paper: {
    position: 'absolute',
    zIndex: 1,
    marginTop: theme.spacing.unit,
    left: 0,
    right: 0,
  },
  divider: {
    height: theme.spacing.unit * 2,
  },
  indicator:{
    background: 'transparent',
  },
  checkboxInputMenu: {
    position: 'relative',
    display: 'flex',
    width: '100%',

    '& input': {
      width: '13px !important',
      height: '13px !important',
      top: '15px',
      left: '15px',
      position: 'absolute',
      margin: '0',
    },
    '& .labelText': {
      padding: "10px 10px 10px 40px",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      display: "inline-block",
      width:'calc(100% + 100px)'
    }
  }
});

function NoOptionsMessage(props: any) {
  return (
    <Typography color='textSecondary' className={props.selectProps.classes.noOptionsMessage} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function inputComponent({ inputRef, ...props }: any) {
  return <div ref={inputRef} {...props} />;
}

function Control(props: any) {
  return (
    <TextField
      fullWidth
      disableUnderline={true}
      InputProps={{
        disableUnderline: true,
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}

function Option(props: any) {
  const source: any = props.selectProps.source;
  return (
    source === 'addConceptOrChapter' ? (
      <div className={props.selectProps.classes.checkboxInputMenu}>
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
        />
        <MenuItem
          buttonRef={props.innerRef}
          selected={props.isFocused}
          component='div'
          className='labelText'
          style={{
            fontWeight: props.isSelected ? 500 : 400,
          }}
          {...props.innerProps}
        >
          {props.children}
        </MenuItem>
      </div>
    ) : (
      <MenuItem
        buttonRef={props.innerRef}
        selected={props.isFocused}
        component='div'
        style={{
          fontWeight: props.isSelected ? 500 : 400,
        }}
        {...props.innerProps}
      >
        {props.children}
      </MenuItem>
    )
  );
}

function Placeholder(props: any) {
  return (
    <Typography color='textSecondary' className={props.selectProps.classes.placeholder} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function SingleValue(props: any) {
  return (
    <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function ValueContainer(props: any) {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

function MultiValue(props: any) {
  if (props.data.id === SELECT_ALL.VALUE) {
    return null;
  }
  const currentIndex = props.index;
  const selectedOptionsList = ((props.selectProps && props.selectProps.value) || []);
  const selectAllOptionSelected = selectedOptionsList.some((data: any) => data.id === SELECT_ALL.VALUE);
  if(props.data.isRemovable){
    
    return (
      <Chip
        tabIndex={-1}
        label={props.children}
        className={classNames(props.selectProps.classes.chip, {
          [props.selectProps.classes.chipFocused]: props.isFocused,
        })}
        onDelete={props.removeProps.onClick}
        deleteIcon={<CancelIcon {...props.removeProps} />}
      />
    );
  } else {
    let activeComponent = (<></>);
    if (currentIndex === (QUESTION_CREATE_REPORTS.MAX_OPTION + selectAllOptionSelected)) {
      activeComponent = (<>+{selectedOptionsList.length - QUESTION_CREATE_REPORTS.MAX_OPTION - selectAllOptionSelected}</>);
    } else if (currentIndex < (QUESTION_CREATE_REPORTS.MAX_OPTION + selectAllOptionSelected)) {
      activeComponent = (
        <Chip
          tabIndex={-1}
          label={props.children}
          className={classNames(props.selectProps.classes.chip, {
            [props.selectProps.classes.chipFocused]: props.isFocused,
          })}
        />
      )
    }
    return activeComponent;
  }
  
}

function Menu(props: any) {
  return (
    <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
}

let components: any = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

interface Props {
  classes: any;
  theme: any;
  suggestions: any[];
  placeholderText: '';
  onSelection: Function;
  loggedInUserFullName: string;
  courses?: any;
  values:{label:string,value:string,id:string} [],
  disabled?: boolean,
  textFieldProps: { [key: string]: any };
  notFound: string;
  isClearable: boolean;
  backspaceRemoves: boolean;
  source: string;
  isMulti?: boolean;
}

class SearchBarWithChips extends React.Component<Props> {
  state = {
    single: null,
    multi: this.props.values || null,
    suggestions: [],
    disabled: true,
    refreshState: false
  };
  componentDidMount() {
    // 
    if (this.props.suggestions.length > 0) {
      const updatedSuggestions = this.prepareSuggestionList(this.props.suggestions);
      this.setState({
        suggestions: updatedSuggestions,
      });
    }
  }
  componentDidUpdate(prevProps: Props) {
      if(prevProps.suggestions !== this.props.suggestions){
           const updatedSuggestions = this.prepareSuggestionList(this.props.suggestions);
          this.setState({
            suggestions: updatedSuggestions
          })
        
      }

    if (prevProps.loggedInUserFullName !== this.props.loggedInUserFullName) {
      const firstChip = {
        value: `${this.props.loggedInUserFullName}`,
        label: `${this.props.loggedInUserFullName}`,
        id: null,
      };
      this.setState({
        multi: [firstChip],
      });
    }
    if (prevProps.values !== this.props.values) {
      this.setState({
        multi: this.props.values
      })
    }

    if (prevProps.disabled !== this.props.disabled) {
      this.setState({ disabled: this.props.disabled, refreshState: true })
      setTimeout(() => {
        this.setState({ refreshState: false });
      }, 100);
    }
  }

  checkListItem = (first: any[], second: any) => {
    return (first || []).find((option: any) => !(second || []).some((data: any) => data.id === option.id))
  }

  handleChange = (name: any) => (value: any) => {
    const optionList = this.getOptions();
    const selectedOptionList = this.state.multi || [];
    const selectedOption = this.checkListItem(selectedOptionList, value) || this.checkListItem(value, selectedOptionList);
    const isSelectAllOption = selectedOption && selectedOption.id === SELECT_ALL.VALUE;
    const hasSelectAllOption = (optionList || []).find((option: any) => option.id === SELECT_ALL.VALUE);
    if (isSelectAllOption) {
      value = selectedOptionList.length === optionList.length ? [] : optionList;
    } else {
      value = (value || []).filter((data: any) => data.id !== 'selectAll');
      if (value.length + 1 === optionList.length && hasSelectAllOption) {
        value = optionList;
      }
    }
   this.setState(
      {
        [name]: value,
      },
      () => this.props.onSelection(this.state.multi)
    );
  };

  handledropdownstate = () => {
    this.setState({ disabled: this.props.disabled })
  }

  prepareSuggestionList = (suggestions: any[]) => {
    return suggestions.map((suggestion) => ({
      ...suggestion,
      value: suggestion.value || (suggestion.firstName + ' ' + suggestion.lastName),
      label: suggestion.label || (suggestion.firstName + ' ' + suggestion.lastName),
      id: suggestion.id || suggestion._id
    }));
  };
  getOptions = () => {
    return this.props.courses ? this.props.courses : this.state.suggestions;
  };
  render() {
    const { classes, theme, textFieldProps = {}, notFound, isClearable, backspaceRemoves, isMulti } = this.props;

    const selectStyles = {
      input: (base: any) => ({
        ...base,
        color: theme.palette.text.primary,
        '& input': {
          font: 'inherit',
        },
      }),
    };
    const hideSelectedOptions = !(this.props.source === 'addConceptOrChapter');


    if (this.state.refreshState) {
      return (<></>);
    }

    return (
      <div className={classes.root}>
        <NoSsr>
          <Select
            classes={classes}
            styles={selectStyles}
            textFieldProps={{
              label: '',
              InputLabelProps: {
                shrink: true,
              },
              ...textFieldProps
            }}
            options={this.getOptions()}
            components={components}
            value={this.state.multi}
            onChange={this.handleChange('multi')}
            placeholder={this.props.placeholderText}
            isDisabled = {this.props.disabled ? this.props.disabled : false}
            isMulti= {isMulti !== undefined ? isMulti : true}
            noOptionsMessage={() => notFound}
            isClearable={isClearable}
            backspaceRemovesValue={backspaceRemoves? backspaceRemoves : false}
            hideSelectedOptions={hideSelectedOptions}
            source={this.props.source}
          />
        </NoSsr>
      </div>
    );
  }
}

// SearchBarWithChips.propTypes = {
//   classes: PropTypes.object.isRequired,
//   theme: PropTypes.object.isRequired,
// };

// @ts-ignore
export default withStyles(styles, { withTheme: true })(SearchBarWithChips);
